// src/pages/VnbVerwaltung.jsx
// VNB-Verwaltung (Verteilnetzbetreiber): Liste + Anlegen + Bearbeiten + Löschen.

import { useState } from 'react';
import { db } from '../lib/supabase';
import { useVnben } from '../hooks/useVnb';
import { C, Field, TextInput, Row, zurueckStil } from './formui';

const EMPTY = {
  name: '', strasse: '', plz: '', ort: '', bundesland: '',
  telefon_zentrale: '', ansprechpartner: '', position: '',
  telefon_durchwahl: '', mobil: '', ansprechpartner_email: '',
  email: '', webseite: '', anmerkung: '',
};

export default function VnbVerwaltung({ onBack }) {
  const [search, setSearch] = useState('');
  const { vnben, count, loading, error, page, setPage, hasNext, hasPrev, totalPages, reload } =
    useVnben({ search, pageSize: 50 });

  const [editing, setEditing] = useState(null); // null | object
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (v) => setEditing({ ...v });

  const cancel = () => { setEditing(null); setMsg(null); };

  const save = async () => {
    if (!editing.name?.trim()) {
      setMsg({ type: 'error', text: 'Bitte Name (Energieversorger) eingeben.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    console.log('[VNB Save] start', editing);

    // Timeout damit es nicht ewig haengt
    // So lang wie die zentrale Zeitgrenze: Bei 8 s lief die Anfrage weiter, und ein
    // zweiter Klick legte den VNB doppelt an.
    const timeoutMs = 60000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Speichern hat ' + (timeoutMs/1000) + 's gedauert - vermutlich RLS-Problem oder Netz weg.')), timeoutMs));

    try {
      const payload = { ...editing, name: editing.name.trim() };
      // Saubere Empty-String -> NULL Konvertierung damit DB nicht meckert
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      const result = await Promise.race([db.vnb.save(payload), timeoutPromise]);
      const { data, error: e } = result || {};
      console.log('[VNB Save] response', { data, error: e });
      if (e) throw e;
      if (!data) throw new Error('Keine Antwort vom Server. RLS verhindert Read-Back?');
      setMsg({ type: 'success', text: 'VNB gespeichert.' });
      setEditing(null);
      reload();
    } catch (e) {
      console.error('[VNB Save] Fehler:', e);
      setMsg({ type: 'error', text: 'Speichern fehlgeschlagen: ' + (e.message || 'Unbekannt') });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (vnb) => {
    if (!window.confirm('VNB "' + vnb.name + '" wirklich löschen / deaktivieren?')) return;
    const { error: e } = await db.vnb.delete(vnb.id);
    if (e) {
      setMsg({ type: 'error', text: 'Fehler: ' + e.message });
    } else {
      setMsg({ type: 'success', text: 'VNB deaktiviert.' });
      reload();
    }
  };

  const set = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '14px', boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={zurueckStil}>‹ Zurück</button>
          <div>
            <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>VNB-Verwaltung</div>
            <div style={{ color: C.gray300, fontSize: 14 }}>Verteilnetzbetreiber pflegen</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 14px' }}>

        {msg && (
          <div style={{ padding: 12, marginBottom: 14, borderRadius: 8,
            background: msg.type === 'error' ? C.redBg : C.greenBg,
            color: msg.type === 'error' ? C.red : C.green, fontSize: 15, fontWeight: 600 }}>
            {msg.type === 'error' ? '! ' : 'OK '}{msg.text}
          </div>
        )}

        {/* === Editor === */}
        {editing && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)', marginBottom: 20, border: '2px solid ' + C.yellow }}>
            <div style={{ fontWeight: 800, color: C.navy, fontSize: 16, marginBottom: 14 }}>
              {editing.id ? 'VNB bearbeiten' : '+ Neuer VNB'}
            </div>
            <Field label='Energieversorger (Name)' required>
              <TextInput value={editing.name} onChange={v => set('name', v)}/>
            </Field>
            <Row columns={3}>
              <Field label='Strasse'><TextInput value={editing.strasse} onChange={v => set('strasse', v)}/></Field>
              <Field label='PLZ'><TextInput value={editing.plz} onChange={v => set('plz', v)}/></Field>
              <Field label='Ort'><TextInput value={editing.ort} onChange={v => set('ort', v)}/></Field>
            </Row>
            <Row columns={2}>
              <Field label='Bundesland'><TextInput value={editing.bundesland} onChange={v => set('bundesland', v)}/></Field>
              <Field label='Webseite'><TextInput value={editing.webseite} onChange={v => set('webseite', v)} type='url'/></Field>
            </Row>
            <Row columns={2}>
              <Field label='Telefon Zentrale'><TextInput value={editing.telefon_zentrale} onChange={v => set('telefon_zentrale', v)}/></Field>
              <Field label='E-Mail'><TextInput value={editing.email} onChange={v => set('email', v)} type='email'/></Field>
            </Row>
            <div style={{ borderTop: '2px solid ' + C.gray100, paddingTop: 14, marginTop: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.gray700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Ansprechpartner</div>
              <Row columns={2}>
                <Field label='Ansprechpartner'><TextInput value={editing.ansprechpartner} onChange={v => set('ansprechpartner', v)}/></Field>
                <Field label='Position'><TextInput value={editing.position} onChange={v => set('position', v)} placeholder='z.B. Stationsbetreuer'/></Field>
              </Row>
              <Row columns={2}>
                <Field label='Telefon Durchwahl'><TextInput value={editing.telefon_durchwahl} onChange={v => set('telefon_durchwahl', v)}/></Field>
                <Field label='Mobil'><TextInput value={editing.mobil} onChange={v => set('mobil', v)}/></Field>
              </Row>
              <Field label='E-Mail (Ansprechpartner)'><TextInput value={editing.ansprechpartner_email} onChange={v => set('ansprechpartner_email', v)} type='email'/></Field>
            </div>
            <Field label='Anmerkung'><TextInput value={editing.anmerkung} onChange={v => set('anmerkung', v)}/></Field>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button onClick={cancel} disabled={saving} style={{ padding: '10px 16px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Abbrechen</button>
              <button onClick={save} disabled={saving} style={{ padding: '10px 22px', background: saving ? C.gray300 : C.yellow, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>{saving ? 'Speichert...' : 'Speichern'}</button>
            </div>
          </div>
        )}

        {/* === Liste === */}
        {!editing && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              <input type='text' value={search} onChange={e => setSearch(e.target.value)} placeholder='VNB suchen...' style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 16, outline: 'none' }}/>
              <button onClick={startNew} style={{ padding: '10px 18px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 15 }}>+ Neuer VNB</button>
            </div>

            {loading ? (
              <div style={{ padding: 30, textAlign: 'center', color: C.gray500 }}>Lade VNBs...</div>
            ) : error ? (
              <div style={{ padding: 14, background: C.redBg, color: C.red, borderRadius: 8 }}>! {error}</div>
            ) : vnben.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: C.gray500 }}>Keine VNBs gefunden. Klick auf "+ Neuer VNB" zum Anlegen.</div>
            ) : (
              <>
                <div style={{ fontSize: 14, color: C.gray500, marginBottom: 8 }}>{count} VNBs gesamt</div>
                {vnben.map(v => (
                  <div key={v.id} style={{ padding: 14, marginBottom: 10, background: C.gray100, borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ fontWeight: 800, color: C.navy, fontSize: 14 }}>{v.name}</div>
                      <div style={{ fontSize: 14, color: C.gray500, marginTop: 2 }}>
                        {[v.strasse, [v.plz, v.ort].filter(Boolean).join(' '), v.bundesland].filter(Boolean).join(', ')}
                      </div>
                      {v.webseite && (
                        <div style={{ fontSize: 14, color: C.navyMid, marginTop: 2 }}>
                          <a href={v.webseite} target='_blank' rel='noreferrer' style={{ color: C.navyMid }}>{v.webseite}</a>
                        </div>
                      )}
                      {v.ansprechpartner && (
                        <div style={{ fontSize: 14, color: C.gray500, marginTop: 2 }}>
                          {v.ansprechpartner}{v.position ? ' (' + v.position + ')' : ''}
                          {v.telefon_durchwahl ? ' - Tel ' + v.telefon_durchwahl : ''}
                          {v.mobil ? ' - Mobil ' + v.mobil : ''}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => startEdit(v)} style={{ padding: '7px 14px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Bearbeiten</button>
                      <button onClick={() => remove(v)} style={{ padding: '7px 14px', background: C.redBg, color: C.red, border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Löschen</button>
                    </div>
                  </div>
                ))}

                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={!hasPrev} style={{ padding: '8px 14px', borderRadius: 7, background: hasPrev ? C.navy : C.gray200, color: hasPrev ? C.yellow : C.gray500, border: 'none', cursor: hasPrev ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700 }}>&lt; Vorherige</button>
                    <span style={{ fontSize: 14, color: C.gray500 }}>Seite {page + 1} / {totalPages}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={!hasNext} style={{ padding: '8px 14px', borderRadius: 7, background: hasNext ? C.navy : C.gray200, color: hasNext ? C.yellow : C.gray500, border: 'none', cursor: hasNext ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700 }}>Nächste &gt;</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
